/**
 * AccountModal — Add/Edit monitored account form (Pattern 8.4 / NFR-A11Y-01, 02).
 *
 * Portalled to document.body to avoid stacking-context issues.
 * Focus trap: Tab/Shift+Tab cycles within modal focusable children.
 * Focus restore: stores trigger element on mount, returns focus on unmount.
 * Escape key closes the modal.
 *
 * Validation (BR-U5-25 through BR-U5-30):
 *   - accountId: must match ^\d{12}$
 *   - roleArn: must match arn:aws:iam::<12-digit>:role/<name>
 *   - friendlyName: 1–100 characters
 *   - regions: at least one required
 *
 * Security (SECURITY-01 / NFR-SEC-FE-01):
 *   - roleArn is IAM configuration, not a runtime credential — safe to log/store
 *   - No tokens or secrets in request bodies
 */
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { MonitoredAccount, MonitoredAccountRequest } from '@/types';
import type { AccountApiError } from '@/api/accountsApi';
import styles from './AccountModal.module.css';

// ── Validation helpers ────────────────────────────────────────────────────────

export function validateAccountId(value: string): string | null {
  return /^\d{12}$/.test(value) ? null : 'Must be exactly 12 digits';
}

export function validateRoleArn(value: string): string | null {
  return /^arn:aws:iam::\d{12}:role\/.+$/.test(value)
    ? null
    : 'Must be in format: arn:aws:iam::<account-id>:role/<role-name>';
}

// ── Focus trap helper ─────────────────────────────────────────────────────────

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// ── Component ─────────────────────────────────────────────────────────────────

export interface AccountModalProps {
  /** Account to edit; undefined for create mode */
  account?: MonitoredAccount;
  onClose: () => void;
  onSubmit: (request: MonitoredAccountRequest) => void;
  isSubmitting: boolean;
  submitError: AccountApiError | Error | null;
}

const AWS_REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
  'ca-central-1',
  'eu-west-1', 'eu-west-2', 'eu-west-3', 'eu-central-1', 'eu-north-1',
  'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1', 'ap-northeast-2', 'ap-south-1',
  'sa-east-1',
];

export function AccountModal({
  account,
  onClose,
  onSubmit,
  isSubmitting,
  submitError,
}: AccountModalProps) {
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Form state
  const [accountId, setAccountId] = useState(account?.accountId ?? '');
  const [roleArn, setRoleArn] = useState(account?.roleArn ?? '');
  const [friendlyName, setFriendlyName] = useState(account?.friendlyName ?? '');
  const [regions, setRegions] = useState<string[]>(account?.regions ?? []);
  const [enabled, setEnabled] = useState(account?.enabled ?? true);

  // Client-side validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Focus trap + restore
  useEffect(() => {
    triggerRef.current = document.activeElement as HTMLElement;
    firstFieldRef.current?.focus();
    return () => {
      triggerRef.current?.focus();
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !modalRef.current) return;

    const focusable = Array.from(
      modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const toggleRegion = (region: string) => {
    setRegions((prev) =>
      prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region],
    );
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    const accErr = validateAccountId(accountId);
    if (accErr) errors.accountId = accErr;
    const arnErr = validateRoleArn(roleArn);
    if (arnErr) errors.roleArn = arnErr;
    if (!friendlyName.trim() || friendlyName.length > 100) {
      errors.friendlyName = 'Required, 1–100 characters';
    }
    if (regions.length === 0) {
      errors.regions = 'At least one region is required';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({ accountId, roleArn, friendlyName, regions, enabled });
  };

  // Surface server-side field errors from AccountApiError
  const serverFieldErrors =
    submitError && 'fieldErrors' in submitError && submitError.fieldErrors
      ? submitError.fieldErrors
      : {};
  const mergedErrors = { ...fieldErrors, ...serverFieldErrors };

  const isEdit = account !== undefined;

  return ReactDOM.createPortal(
    <div
      className={styles.overlay}
      aria-hidden="false"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={styles.modal}
        onKeyDown={handleKeyDown}
      >
        <header className={styles.modalHeader}>
          <h2 id="modal-title" className={styles.modalTitle}>
            {isEdit ? 'Edit Account' : 'Add Account'}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close dialog"
          >
            ×
          </button>
        </header>

        {submitError && !('fieldErrors' in submitError && submitError.fieldErrors) && (
          <div role="alert" className={styles.errorBanner}>
            {submitError.message}
          </div>
        )}

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.field}>
            <label htmlFor="modal-accountId" className={styles.label}>
              AWS Account ID <span aria-hidden="true">*</span>
            </label>
            <input
              ref={firstFieldRef}
              id="modal-accountId"
              type="text"
              className={`${styles.input} ${mergedErrors.accountId ? styles.inputError : ''}`}
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="123456789012"
              maxLength={12}
              disabled={isEdit}
              aria-describedby={mergedErrors.accountId ? 'modal-accountId-error' : undefined}
              aria-invalid={!!mergedErrors.accountId}
              required
            />
            {mergedErrors.accountId && (
              <span id="modal-accountId-error" role="alert" className={styles.fieldError}>
                {mergedErrors.accountId}
              </span>
            )}
            {isEdit && (
              <span className={styles.hint}>Account ID cannot be changed after creation.</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="modal-roleArn" className={styles.label}>
              IAM Role ARN <span aria-hidden="true">*</span>
            </label>
            <input
              id="modal-roleArn"
              type="text"
              className={`${styles.input} ${mergedErrors.roleArn ? styles.inputError : ''}`}
              value={roleArn}
              onChange={(e) => setRoleArn(e.target.value)}
              placeholder="arn:aws:iam::123456789012:role/SystemMonitorRole"
              aria-describedby={mergedErrors.roleArn ? 'modal-roleArn-error' : undefined}
              aria-invalid={!!mergedErrors.roleArn}
              required
            />
            {mergedErrors.roleArn && (
              <span id="modal-roleArn-error" role="alert" className={styles.fieldError}>
                {mergedErrors.roleArn}
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="modal-friendlyName" className={styles.label}>
              Friendly Name <span aria-hidden="true">*</span>
            </label>
            <input
              id="modal-friendlyName"
              type="text"
              className={`${styles.input} ${mergedErrors.friendlyName ? styles.inputError : ''}`}
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
              placeholder="Production Account"
              maxLength={100}
              aria-describedby={mergedErrors.friendlyName ? 'modal-friendlyName-error' : undefined}
              aria-invalid={!!mergedErrors.friendlyName}
              required
            />
            {mergedErrors.friendlyName && (
              <span id="modal-friendlyName-error" role="alert" className={styles.fieldError}>
                {mergedErrors.friendlyName}
              </span>
            )}
          </div>

          <div className={styles.field}>
            <fieldset className={styles.fieldset}>
              <legend className={styles.label}>
                AWS Regions <span aria-hidden="true">*</span>
              </legend>
              <div className={styles.regionGrid}>
                {AWS_REGIONS.map((region) => (
                  <label key={region} className={styles.regionLabel}>
                    <input
                      type="checkbox"
                      checked={regions.includes(region)}
                      onChange={() => toggleRegion(region)}
                      className={styles.regionCheckbox}
                    />
                    {region}
                  </label>
                ))}
              </div>
              {mergedErrors.regions && (
                <span role="alert" className={styles.fieldError}>
                  {mergedErrors.regions}
                </span>
              )}
            </fieldset>
          </div>

          <div className={styles.field}>
            <label className={styles.enabledLabel}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className={styles.enabledCheckbox}
              />
              Enable collection for this account
            </label>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Account'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
